import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Download, Package, CheckCircle2, FileText, Info } from "lucide-react";
import axios from "axios";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products/${slug}`);
        setProduct(res.data);
      } catch { setProduct(null); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  useSEO({
    title: product ? `${product.name} | Prodotti Fotovoltaici` : "Prodotto | Fotovoltaico Chiavi in Mano",
    description: product?.description || "",
    ogImage: product?.images?.[0] || "",
    canonical: `${window.location.origin}/prodotti/${slug}`,
  });

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!product) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" data-testid="product-not-found">
      <h1 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Prodotto non trovato</h1>
      <button onClick={() => navigate("/prodotti")} className="bg-emerald-500 text-white font-bold rounded-full px-6 py-2.5 text-sm">Torna al Catalogo</button>
    </div>
  );

  const specLines = product.specs ? product.specs.split("\n").filter((l) => l.trim()) : [];

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Logo" className="h-14 w-auto object-contain" />
            <span className="hidden sm:block text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Fotovoltaico Chiavi in Mano</span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/prodotti")} className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1.5" data-testid="product-back">
              <ArrowLeft className="w-4 h-4" /> Catalogo
            </button>
            <button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg shadow-emerald-500/25">
              Richiedi Preventivo
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button onClick={() => navigate("/prodotti")} className="text-sm text-slate-400 hover:text-emerald-600 flex items-center gap-1 mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna al catalogo
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Image - 2 columns */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden bg-slate-50 aspect-[4/3] border border-slate-100" data-testid="product-main-image">
              {product.images?.[0] ? (
                <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-4" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Package className="w-16 h-16 text-slate-200" /></div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {product.images.slice(1, 5).map((img, i) => (
                  <div key={i} className="rounded-lg overflow-hidden bg-slate-50 aspect-square border border-slate-100">
                    <img src={img} alt="" className="w-full h-full object-contain p-1" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details - 3 columns */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-3">
              {product.category && <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{product.category}</span>}
              {product.brand && <span className="text-sm text-slate-400 font-medium">{product.brand}</span>}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="product-name">{product.name}</h1>
            <p className="mt-4 text-base text-slate-500 leading-relaxed">{product.description}</p>

            {/* Specs Table */}
            {specLines.length > 0 && (
              <div className="mt-8" data-testid="product-specs">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-slate-400" /> Specifiche Tecniche
                </h3>
                <div className="bg-slate-50 rounded-xl border border-slate-100 divide-y divide-slate-100 overflow-hidden">
                  {specLines.map((line, i) => {
                    const parts = line.split(":");
                    const label = parts[0]?.trim();
                    const value = parts.slice(1).join(":").trim();
                    return (
                      <div key={i} className={`flex items-center text-sm ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                        <div className="font-semibold text-slate-700 px-5 py-3 w-1/2 sm:w-2/5">{label}</div>
                        <div className="text-slate-500 px-5 py-3 flex-1">{value || line}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sectors */}
            {product.sectors?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide mb-2">Adatto per</h3>
                <div className="flex flex-wrap gap-2">
                  {product.sectors.map((s) => (
                    <span key={s} className="text-xs font-medium bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 flex flex-wrap gap-3">
              {product.brochure_url && (
                <a href={product.brochure_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-full px-6 py-3 text-sm btn-scale shadow-lg shadow-blue-500/25 transition-colors"
                  data-testid="product-brochure-download">
                  <Download className="w-4 h-4" /> Scarica Brochure PDF
                </a>
              )}
              <button onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-3 text-sm btn-scale shadow-lg shadow-emerald-500/25 transition-colors"
                data-testid="product-cta">
                Richiedi Preventivo
              </button>
            </div>

            {/* Info Note */}
            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-3" data-testid="product-info-note">
              <FileText className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Per maggiori dettagli</p>
                <p className="text-xs text-amber-600 mt-0.5">Scarica la brochure completa o contattaci per ricevere tutte le specifiche tecniche e un preventivo su misura.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-8 bg-slate-950 text-center mt-16">
        <p className="text-xs text-slate-500">&copy; 2025 Fotovoltaico Chiavi in Mano. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}
